import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Loader2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function StudentInternships() {
  const { user, formatApiError } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", college: "", skills: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    load();
  }, []);
  const load = async () => {
    try {
      const r = await api.get("/public/internships");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  const openApply = (i) => {
    setSelected(i);
    setForm({
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      college: user?.college || "",
      skills: user?.skills || "",
    });
  };

  const apply = async () => {
    setSubmitting(true);
    try {
      await api.post("/student/internships/apply", { internship_id: selected.id, ...form });
      toast.success("Application submitted!");
      setSelected(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <h1 className="heading text-2xl font-bold">Internship Programs</h1>
      <p className="text-slate-500 text-sm mt-1">Apply and earn a verifiable certificate.</p>
      {loading ? (
        <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="mt-8 p-14 rounded-2xl border border-dashed border-slate-300 text-center">
          <Briefcase size={40} className="mx-auto text-slate-300" />
          <div className="mt-3 text-slate-500">No internships open right now.</div>
        </div>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((i) => (
            <div key={i.id} className="p-6 rounded-2xl border border-slate-200 bg-white" data-testid={`internship-card-${i.id}`}>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#F59E0B] font-bold">{i.duration}</div>
              <h3 className="heading text-xl font-bold mt-2">{i.title}</h3>
              <p className="mt-2 text-sm text-slate-600 line-clamp-3">{i.description}</p>
              {i.skills && <div className="mt-3 text-xs text-slate-500">Skills: {i.skills}</div>}
              <button
                onClick={() => openApply(i)}
                className="mt-4 bg-brand hover:bg-[#172b66] text-white font-semibold rounded-full px-5 py-2 text-sm"
                data-testid={`apply-internship-${i.id}-button`}
              >
                Apply
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply: {selected?.title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            {[
              ["Full Name", "name"],
              ["Email", "email"],
              ["Phone", "phone"],
              ["College", "college"],
              ["Skills", "skills"],
            ].map(([label, key]) => (
              <div key={key}>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
                <input
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand outline-none"
                  data-testid={`apply-${key}-input`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setSelected(null)} className="px-4 py-2 text-sm font-semibold" data-testid="apply-cancel-button">Cancel</button>
            <button
              onClick={apply}
              disabled={submitting}
              className="bg-brand text-white font-semibold rounded-lg px-5 py-2 text-sm disabled:opacity-60"
              data-testid="apply-submit-button"
            >
              {submitting ? "Applying..." : "Submit Application"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
